import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

interface ColorSlotProps {
  /** Hex, or null where the gym has not said what colour this is. */
  value: string | null;
  label: string;
  disabled?: boolean;
  /** '' clears the slot; a hex string sets it. */
  onChange: (value: string) => void;
}

/** What a colour input opens on when the slot is still empty. */
const UNSET_SEED = '#cccccc';

/**
 * One of a hold type's two colours.
 *
 * Both are optional, so "not set" has to be a state you can see and get back to:
 * an empty slot is a dashed outline rather than a black well (a native colour
 * input has no empty value and would otherwise claim the type is black), and a
 * filled one carries the cross that takes the colour off again.
 */
export function ColorSlot({ value, label, disabled = false, onChange }: ColorSlotProps) {
  const { t } = useTranslation();

  return (
    <span className="relative inline-flex items-center">
      <input
        type="color"
        value={value ?? UNSET_SEED}
        disabled={disabled}
        aria-label={label}
        title={label}
        onChange={(event) => onChange(event.target.value)}
        className={`size-7 cursor-pointer rounded-md border bg-transparent p-0.5 disabled:cursor-not-allowed disabled:opacity-40 ${
          value ? 'border-border' : 'border-dashed border-muted-foreground/60 opacity-50'
        }`}
      />
      {value && !disabled && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label={t('gymAdmin.holdTypes.clearColor', { label })}
          className="absolute -right-1 -top-1 rounded-full bg-background text-muted-foreground shadow-sm hover:text-destructive"
        >
          <X className="size-3" />
        </button>
      )}
    </span>
  );
}
